import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTransactions, addTransaction, deleteTransaction, getCards } from '../api';
import {
  formatNumber,
  parseFormattedNumber,
  convertDateForApi,
  formatDateForDisplay,
  getTodayByLocale,
  getMonthStartByLocale,
  getDateConfig,
  switchDateLocale,
  usePagination,
} from '../utils';
import DatePicker from 'react-multi-date-picker';
import Select from 'react-select';

function Transactions({ dateLocale }) {
  const dateConfig = getDateConfig(dateLocale);
  const [transactions, setTransactions] = useState([]);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    card_id: '',
    amount: '',
    transaction_date: getTodayByLocale(dateLocale),
    description: '',
  });
  const [filters, setFilters] = useState({
    card_id: '',
    startDate: getMonthStartByLocale(dateLocale),
    endDate: getTodayByLocale(dateLocale),
  });
  const prevLocaleRef = useRef(dateLocale);
  const transactionsPagination = usePagination(transactions);
  const goToTransactionsPage = transactionsPagination.goToPage;

  const renderPaginationControls = (pagination) => {
    if (pagination.totalItems === 0) {
      return null;
    }

    return (
      <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between border-top px-3 py-3 gap-3">
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted small">تعداد در صفحه</span>
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pagination.pageSize}
            onChange={(e) => pagination.setPageSize(Number(e.target.value))}
          >
            {pagination.pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-muted small">
            نمایش {pagination.rangeStart} تا {pagination.rangeEnd} از {pagination.totalItems}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.prevPage}
            disabled={!pagination.canGoPrev}
          >
            قبلی
          </button>
          <span className="text-muted small">
            صفحه {pagination.currentPage} از {pagination.totalPages}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={pagination.nextPage}
            disabled={!pagination.canGoNext}
          >
            بعدی
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const prevLocale = prevLocaleRef.current;
    if (prevLocale !== dateLocale) {
      setFormData((prev) => ({
        ...prev,
        transaction_date: switchDateLocale(prev.transaction_date, prevLocale, dateLocale),
      }));
      setFilters((prev) => ({
        ...prev,
        startDate:
          prev.startDate
            ? switchDateLocale(prev.startDate, prevLocale, dateLocale)
            : getMonthStartByLocale(dateLocale),
        endDate:
          prev.endDate
            ? switchDateLocale(prev.endDate, prevLocale, dateLocale)
            : getTodayByLocale(dateLocale),
      }));
      prevLocaleRef.current = dateLocale;
    }
  }, [dateLocale]);

  const loadData = useCallback(
    async (currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {};
      if (currentFilters.card_id) {
        params.card_id = currentFilters.card_id;
      }
      const startDateValue = convertDateForApi(currentFilters.startDate, dateLocale);
      if (startDateValue) {
        params.start_date = startDateValue;
      }
      const endDateValue = convertDateForApi(currentFilters.endDate, dateLocale);
      if (endDateValue) {
        params.end_date = endDateValue;
      }
      const [transactionsRes, cardsRes] = await Promise.all([getTransactions(params), getCards()]);
      setTransactions(transactionsRes.data);
      setCards(cardsRes.data);
      setError('');
    } catch (err) {
      setError('خطا در بارگذاری اطلاعات');
    } finally {
      setLoading(false);
    }
    },
    [filters, dateLocale]
  );

  useEffect(() => {
    loadData(filters);
  }, [filters, loadData]);

  useEffect(() => {
    goToTransactionsPage(1);
  }, [transactions, goToTransactionsPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.card_id || !formData.amount || !formData.transaction_date) {
      setError('کارت، مبلغ و تاریخ الزامی است');
      return;
    }

    try {
      const gregorianDate = convertDateForApi(formData.transaction_date, dateLocale);
      await addTransaction({
        card_id: formData.card_id,
        amount: parseFloat(parseFormattedNumber(formData.amount)),
        transaction_date: gregorianDate,
        description: formData.description,
      });
      setSuccess('تراکنش با موفقیت ثبت شد');
      setFormData({
        card_id: '',
        amount: '',
        transaction_date: getTodayByLocale(dateLocale),
        description: '',
      });
      loadData(filters);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در ثبت تراکنش');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('آیا از حذف این تراکنش مطمئن هستید؟')) return;

    try {
      await deleteTransaction(id);
      setSuccess('تراکنش حذف شد');
      loadData(filters);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('خطا در حذف تراکنش');
    }
  };

  const resetFilters = () => {
    setFilters({
      card_id: '',
      startDate: getMonthStartByLocale(dateLocale),
      endDate: getTodayByLocale(dateLocale),
    });
  };

  if (loading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center w-100"
        style={{ minHeight: '50vh' }}
      >
        <div className="spinner-border text-primary me-3" role="status" />
        <span className="fw-bold text-muted">در حال بارگذاری...</span>
      </div>
    );
  }

  return (
    <div className="d-grid gap-4">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setError('')}
          />
        </div>
      )}

      {success && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {success}
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccess('')}
          />
        </div>
      )}

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-transparent border-bottom-0 pb-0">
              <h2 className="h5 mb-1">ثبت تراکنش جدید</h2>
              <p className="text-muted small mb-0">جزئیات تراکنش را وارد کنید</p>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="row g-3">
                <div className="col-12">
                  <label className="form-label">کارت</label>
                  <Select
                    options={cards.map((card) => ({
                      value: card.id,
                      label: `${card.card_number} - ${card.bank_name} (${card.person_name})`,
                    }))}
                    value={
                      formData.card_id
                        ? cards
                            .filter((card) => card.id.toString() === formData.card_id.toString())
                            .map((card) => ({
                              value: card.id,
                              label: `${card.card_number} - ${card.bank_name} (${card.person_name})`,
                            }))[0]
                        : null
                    }
                    onChange={(selectedOption) =>
                      setFormData({
                        ...formData,
                        card_id: selectedOption ? selectedOption.value : '',
                      })
                    }
                    placeholder="انتخاب کارت..."
                    isSearchable
                    isClearable
                    noOptionsMessage={() => 'کارتی یافت نشد'}
                    styles={{
                      control: (base) => ({
                        ...base,
                        textAlign: 'right',
                        direction: 'rtl',
                      }),
                      menu: (base) => ({
                        ...base,
                        textAlign: 'right',
                        direction: 'rtl',
                      }),
                      option: (base) => ({
                        ...base,
                        textAlign: 'right',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        textAlign: 'right',
                      }),
                      placeholder: (base) => ({
                        ...base,
                        textAlign: 'right',
                      }),
                      input: (base) => ({
                        ...base,
                        textAlign: 'right',
                      }),
                    }}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">مبلغ (تومان)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.amount}
                    onChange={(e) => {
                      const rawValue = parseFormattedNumber(e.target.value);
                      if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                        setFormData({ ...formData, amount: formatNumber(rawValue) });
                      }
                    }}
                    placeholder="مثلاً ۲۵۰۰۰۰۰"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">تاریخ تراکنش</label>
                  <DatePicker
                    value={formData.transaction_date}
                    onChange={(date) => {
                      if (date) {
                        setFormData({ ...formData, transaction_date: date });
                      }
                    }}
                    calendar={dateConfig.calendar}
                    locale={dateConfig.locale}
                    calendarPosition="bottom-right"
                    inputClass="form-control"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">توضیحات</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="توضیحات تکمیلی (اختیاری)"
                  />
                </div>
                <div className="col-12">
                  <button type="submit" className="btn btn-primary w-100">
                    ثبت تراکنش
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="col-12 col-lg-8">
          <div className="d-grid gap-3">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-bottom-0 pb-0">
                <h2 className="h6 mb-1">فیلتر لیست تراکنش‌ها</h2>
                <p className="text-muted small mb-0">بازه و کارت مدنظر را انتخاب کنید</p>
              </div>
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-12 col-md-4">
                    <label className="form-label">کارت</label>
                    <select
                      className="form-select"
                      value={filters.card_id}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          card_id: e.target.value,
                        }))
                      }
                    >
                      <option value="">همه کارت‌ها</option>
                      {cards.map((card) => (
                        <option key={card.id} value={card.id}>
                          {card.card_number} - {card.bank_name} ({card.person_name})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">از تاریخ</label>
                    <DatePicker
                      value={filters.startDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label">تا تاریخ</label>
                    <DatePicker
                      value={filters.endDate}
                      onChange={(date) =>
                        setFilters((prev) => ({
                          ...prev,
                          endDate: date || null,
                        }))
                      }
                      calendar={dateConfig.calendar}
                      locale={dateConfig.locale}
                      calendarPosition="bottom-right"
                      inputClass="form-control"
                    />
                  </div>
                  <div className="col-12 col-md-2">
                    <button type="button" className="btn btn-outline-secondary w-100" onClick={resetFilters}>
                      بازنشانی
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-footer bg-light border-top">
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted small">جمع مبلغ:</span>
                  <span className="fw-bold text-primary fs-5">
                    {formatNumber(transactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0))}
                    <small className="text-muted ms-1">تومان</small>
                  </span>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-transparent border-bottom-0 pb-0 d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="h5 mb-1">لیست تراکنش‌ها</h2>
                  <p className="text-muted small mb-0">تاریخچه تراکنش‌های ثبت‌شده</p>
                </div>
                <span className="badge bg-primary text-white px-3 py-2">{transactions.length} رکورد</span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th scope="col">ردیف</th>
                        <th scope="col">کارت</th>
                        <th scope="col">بانک</th>
                        <th scope="col">شخص</th>
                        <th scope="col">مبلغ</th>
                        <th scope="col">تاریخ</th>
                        <th scope="col">توضیحات</th>
                        <th scope="col" className="text-end">عملیات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactionsPagination.totalItems === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center text-muted py-4">
                            تراکنشی ثبت نشده است
                          </td>
                        </tr>
                      )}
                      {transactionsPagination.paginatedItems.map((transaction, index) => (
                        <tr key={transaction.id}>
                          <td>
                            <span className="badge bg-secondary">{transactionsPagination.rangeStart + index}</span>
                          </td>
                          <td className="fw-semibold">{transaction.card_number}</td>
                          <td>{transaction.bank_name}</td>
                          <td>{transaction.person_name}</td>
                          <td>
                            <div className="fw-bold text-dark">{formatNumber(transaction.amount)}</div>
                            <small className="text-muted">تومان</small>
                          </td>
                          <td>{formatDateForDisplay(transaction.transaction_date, dateLocale)}</td>
                          <td>{transaction.description || <span className="text-muted">بدون توضیح</span>}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(transaction.id)}
                            >
                              حذف
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderPaginationControls(transactionsPagination)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Transactions;

